/* eslint-disable sort-imports-es6-autofix/sort-imports-es6 */
import { ChartDataXY, ChartSeriesXY } from '../data/chart-data-types';
import { customElement, property } from 'lit/decorators.js';
import { ApexCharts, ApexChartWidget } from './apex-chart-widget';
import { DateRange } from './dashboard-filter';
import { generatedPurchaseOrdersCache } from '../data/generated-purchase-orders-cache';
import { getPurchaseOrderStatusStr } from '../../../purchase-orders/data/purchase-order-helper-functions';
import { PropertyValues } from 'lit';
import { LangGeneratedPurchaseOrdersWidget } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/generated-purchase-orders-widget-lang';
import {
  localDateTimeToServer,
  serverDateTimeToLocalDateTime
} from '../../../../webmodule-common/other/datetime-converter';
// eslint-disable-next-line sort-imports-es6-autofix/sort-imports-es6
import {
  currencyCodeToString,
  moneyToCompactStr,
  stringToCurrencyCode
} from '../../../../webmodule-common/other/currency-formatter';
import {
  PurchaseOrderState,
  ResultGetGeneratedPurchaseOrderRevenue
} from '../../../api/dealer-api-interface-franchisee';
import { TimelineWidgetModal } from './timeline-widget-modal';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: keep explicit import path for interop types
import type { NullPromise, Snippet } from '../../../../webmodule-common/interop/webmodule-interop';

@customElement('generated-purchase-orders-revenue-widget')
export class GeneratedPurchaseOrdersWidget extends ApexChartWidget {
  chart: any | null = null;
  // Track all per-currency charts to be able to destroy/clear on refresh (consistent with quotes)
  private charts: any[] = [];
  private _isDisconnected = false;

  constructor() {
    super('purchase-order-issued-revenue');
  }

  @property({ type: Object }) dateRange?: DateRange;
  @property() branchId?: string;
  @property() userId?: string;
  @property({ type: Array }) states: PurchaseOrderState[] = [
    PurchaseOrderState.Draft,
    PurchaseOrderState.IssuedPending,
    PurchaseOrderState.Issued,
    PurchaseOrderState.Completed
  ];

  private _cache = generatedPurchaseOrdersCache();

  protected async refreshData(): NullPromise<ResultGetGeneratedPurchaseOrderRevenue> {
    if (this.dateRange) {
      const startDate = localDateTimeToServer(this.dateRange.startDate);
      const endDate = localDateTimeToServer(this.dateRange.endDate);
      const result = await this._cache?.getData(startDate, endDate, this.states, this.branchId, this.userId);
      if (result) {
        return result;
      }
    }
    return null;
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.updateData();
  }

  protected getTitle(): string {
    return LangGeneratedPurchaseOrdersWidget.purchaseOrderNetAmount({ label: this.dateRange?.label ?? '' });
  }

  protected getHeader(): Snippet {
    return this.getTitle();
  }

  // Body is provided by ApexChartWidget via constructor class name, keeping consistency with quotes widget

  protected async onChartClick(_event: any, _chartContext: any, config: any, currency: string) {
    const series = config.config.series as ChartSeriesXY[];
    const modal = new TimelineWidgetModal();
    modal.eventTitle = this.getTitle();
    modal.seriesData = series;
    modal.currencySymbol = currency;
    await modal.showModal();
  }

  protected convertDateToMillis(dateStr: string): number {
    let dt = serverDateTimeToLocalDateTime(dateStr);
    dt = dt.set({ hour: 0, minute: 0 });
    return dt.toMillis();
  }

  protected getSeriesColorByState(state: number): string {
    // return the corresponding badge colour
    switch (state as PurchaseOrderState) {
      case PurchaseOrderState.Draft:
        return '#0ea5e9';
      case PurchaseOrderState.IssuedPending:
        return '#a855f7';
      case PurchaseOrderState.Issued:
        return '#eab308';
      case PurchaseOrderState.Completed:
        return '#52525b';
      default:
        return '#3282cc'; // base colour
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._isDisconnected = true;
    try {
      this.chart?.destroy?.();
    } catch (e) {
      /* noop: chart might already be destroyed */
    }
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch (e) {
        /* noop: chart might already be destroyed */
      }
    }
    this.charts = [];
  }

  protected async updateData(): Promise<void> {
    const results = await this.refreshData();
    if (!results) return;

    // Wait for Lit to finish rendering and ensure the element still exists
    await this.updateComplete;
    if (!this.isConnected || this._isDisconnected) return;

    // Destroy any existing charts (single and multi) and clear container
    try {
      this.chart?.destroy?.();
    } catch (e) {
      /* noop: ignore destroy errors */
    }
    this.chart = null;
    for (const c of this.charts) {
      try {
        c?.destroy?.();
      } catch (e) {
        /* noop: ignore destroy errors */
      }
    }
    this.charts = [];

    const parentEl = this.querySelector('.purchase-order-issued-revenue');
    if (!parentEl || !this.isConnected || this._isDisconnected) return;
    parentEl.innerHTML = '';

    // Group revenueSummary by currency (map enum to ISO code string like the issued quotes widget)
    const currencyGroups: Record<string, typeof results.revenueSummary> = {} as any;
    for (const item of results.revenueSummary) {
      const currency = currencyCodeToString((item as any).currency) ?? 'USD';
      (currencyGroups[currency] ??= []).push(item as any);
    }

    // Render a chart for each currency
    Object.entries(currencyGroups).forEach(([currency, revenueSummary]) => {
      if (!this.isConnected || this._isDisconnected) return;

      // Build series for this currency
      const chartSeries: ChartSeriesXY[] = [];
      for (const purchaseOrderState of results.purchaseOrderStates) {
        const chartData: ChartDataXY[] = [];
        const data = revenueSummary.filter(d => d.state === purchaseOrderState || d.state === -1);
        data.forEach(r => chartData.push({ x: this.convertDateToMillis(r.date), y: r.dailyRevenue }));
        chartSeries.push({
          name: LangGeneratedPurchaseOrdersWidget.purchaseOrderStatus({
            status: getPurchaseOrderStatusStr(purchaseOrderState)
          }),
          data: chartData,
          color: this.getSeriesColorByState(purchaseOrderState)
        });
      }

      // Create a container per currency (match issued quotes pattern)
      const container = document.createElement('div');
      container.className = 'purchase-order-issued-revenue-currency';
      const caption = document.createElement('div');
      caption.textContent = `${this.getTitle()} (${currency})`;
      caption.style.fontWeight = '600';
      caption.style.margin = '8px 0';
      container.appendChild(caption);
      parentEl.appendChild(container);

      // Chart options for this currency (match issued quotes)
      const chartOptions = {
        chart: {
          type: 'area',
          height: '179px',
          selection: { enabled: false },
          stacked: false,
          toolbar: {
            tools: {
              download: false,
              customIcons: [
                {
                  icon: '<i class="fa fa-plus-circle fa-lg"></i>',
                  index: 0,
                  title: 'Zoom',
                  class: 'custom-icon',
                  click: (chart: any, opts: any, e: any) => this.onChartClick(e, chart, opts, currency)
                }
              ]
            }
          },
          zoom: { enabled: false }
        },
        stroke: { width: 1 },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } }
        },
        series: [],
        xaxis: { type: 'datetime' },
        yaxis: {
          labels: { formatter: (value: number) => `${moneyToCompactStr(value, stringToCurrencyCode(currency))}` }
        },
        noData: { text: 'Loading...' }
      } as any;

      const options = { ...chartOptions, series: chartSeries };
      const chart = new ApexCharts(container, options);
      chart.render();
      this.charts.push(chart);
    });
  }
}
